import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

// Copy stored as plain strings and rendered via {expressions} so the legal
// text stays byte-for-byte verbatim (no JSX entity escaping applied).
const PAGE_TITLE = 'STAGE8 — PRIVACY NOTICE (WAITLIST)';
const LAST_UPDATED = 'Last updated: 8 September 2026';

const SECTIONS = [
  {
    n: '1',
    title: 'Controller',
    blocks: [
      { type: 'p', text: 'STAGE8, a Dutch eenmanszaak (KVK 95469974), operated by Niels Grootveld.' },
      { type: 'p', text: 'Contact: niels@stage8.io' },
      { type: 'p', text: 'A BV may replace this later; until then this notice is the one that applies.' },
    ],
  },
  {
    n: '2',
    title: 'What we collect',
    blocks: [
      {
        type: 'ul',
        items: [
          'Email',
          'Country',
          'Optional area (city or region — not a street address)',
          'Optional venue or company name',
          'Optional role (musician / booker or venue / tech or other)',
          'Which emails you agreed to (pilot notice and, if you ticked it, launch notes)',
          'Time of signup',
          'IP and basic request logs, only to stop spam',
        ],
      },
      { type: 'p', text: 'We do not collect a street address or phone on this page.' },
      { type: 'p', text: 'We do not run a public directory from this list.' },
    ],
  },
  {
    n: '3',
    title: 'Why, and on what basis',
    blocks: [
      {
        type: 'ul',
        items: [
          'Pilot mail ("the private brief is ready"): consent, checkbox A.',
          'Occasional launch notes: consent, checkbox B. You can refuse B and still join the list.',
          'Country / area / venue name / role: to see where demand is and who to invite. Same consent as the list, or you leave those fields empty.',
          'IP / logs: legitimate interest in stopping abuse. Kept only as long as needed for rate limits and security.',
        ],
      },
      { type: 'p', text: 'We do not sell or rent the list. We do not use it to publish gigs or sell access to talent.' },
    ],
  },
  {
    n: '4',
    title: 'Processors',
    blocks: [
      { type: 'p', text: 'Only processors under a DPA, to host the page and send mail:' },
      {
        type: 'ul',
        items: [
          'Cloudflare (CDN / security)',
          'Render (hosting)',
          'MongoDB Atlas (database)',
          'Resend (email)',
        ],
      },
      { type: 'p', text: 'Resend stores customer/email data in the United States. Cloudflare is a US company. MongoDB Atlas can reside in the EU (e.g. Frankfurt) and is still a US processor. Transfers outside the EEA use their DPAs and the EU Standard Contractual Clauses and/or the EU–US Data Privacy Framework.' },
      { type: 'p', text: 'We do not claim that waitlist data never leaves the EU.' },
    ],
  },
  {
    n: '5',
    title: 'How long',
    blocks: [
      { type: 'p', text: 'Until you unsubscribe or ask us to delete the record, or until we invite you into the product and a full privacy policy replaces this notice.' },
      { type: 'p', text: 'Unused waitlist records are deleted within 12 months after the first invite wave.' },
      { type: 'p', text: 'If the pilot is cancelled, we delete the list within 30 days and update this page.' },
    ],
  },
  {
    n: '6',
    title: 'Your rights',
    blocks: [
      { type: 'p', text: 'Access, correct, delete, restrict, object, portability. Withdraw consent anytime (unsubscribe or email).' },
      { type: 'p', text: 'niels@stage8.io — we reply within one month.' },
      { type: 'p', text: 'Complaint: Autoriteit Persoonsgegevens, autoriteitpersoonsgegevens.nl.' },
    ],
  },
  {
    n: '7',
    title: 'Automated decisions and children',
    blocks: [
      { type: 'p', text: 'No automated decisions. Not directed at children under 16.' },
    ],
  },
  {
    n: '8',
    title: 'Cookies',
    blocks: [
      { type: 'p', text: 'No advertising cookies on this page. Only what we need to serve the page and the form.' },
    ],
  },
  {
    n: '9',
    title: 'Changes',
    blocks: [
      { type: 'p', text: 'Material changes get a new date on this page.' },
    ],
  },
];

const headingStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 16,
  fontWeight: 600,
  color: '#F0EBE0',
  marginBottom: 10,
  lineHeight: 1.4,
};
const paragraphStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 15,
  color: '#F0EBE0',
  lineHeight: 1.65,
};
const listStyle = {
  margin: 0,
  paddingLeft: 22,
  listStyle: 'disc',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const listItemStyle = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: 15,
  color: '#F0EBE0',
  lineHeight: 1.6,
};

export default function PrivacyPage() {
  const navigate = useNavigate();

  const handleClose = () => {
    // navigate('/') instead of navigate(-1): /privacy is often opened
    // directly (waitlist form link, bookmark), where there's no history
    // to go back to — same rationale as LoginPage's close button.
    navigate('/');
  };

  return (
    <div className="privacy-page" style={{ minHeight: '100vh', background: '#0E1110' }} data-testid="privacy-page">
      <button
        onClick={handleClose}
        aria-label="Close"
        data-testid="privacy-close-btn"
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 10,
          padding: 8,
          borderRadius: 'var(--s8-radius-md, 8px)',
          color: '#a39889',
          background: 'rgba(14,17,16,0.6)',
        }}
      >
        <X className="h-5 w-5" />
      </button>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div style={{ maxWidth: 720, margin: '0 auto', paddingTop: 48, paddingBottom: 64 }}>
          <h1
            data-testid="privacy-title"
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: '0.01em',
              color: '#F0EBE0',
              lineHeight: 1.2,
            }}
          >
            {PAGE_TITLE}
          </h1>
          <p
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 13,
              color: '#a39889',
              marginTop: 6,
            }}
          >
            {LAST_UPDATED}
          </p>

          {SECTIONS.map((section) => (
            <section key={section.n} style={{ marginTop: 28 }}>
              <h2 style={headingStyle}>
                {section.n}. {section.title}
              </h2>
              {section.blocks.map((block, i) =>
                block.type === 'ul' ? (
                  <ul key={i} style={{ ...listStyle, marginTop: i === 0 ? 0 : 12 }}>
                    {block.items.map((item, j) => (
                      <li key={j} style={listItemStyle}>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} style={{ ...paragraphStyle, marginTop: i === 0 ? 0 : 12 }}>
                    {block.text}
                  </p>
                )
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
